import React, { useState, useRef, useEffect, useCallback } from 'react';
import { changePassword } from '../utils/api';
import useBodyScrollLock from '../hooks/useBodyScrollLock';

const MIN_LEN = 10;

const overlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.7)',
  backdropFilter: 'blur(4px)',
  zIndex: 300,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20,
};

const modal = {
  background: 'var(--bg-card2)',
  border: '1px solid var(--border-med)',
  borderRadius: 14,
  width: '100%', maxWidth: 420,
  maxHeight: '90vh',
  display: 'flex', flexDirection: 'column',
  boxShadow: 'var(--shadow)',
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 };

const inputStyle = {
  width: '100%',
  height: 48,
  padding: '0 14px',
  background: 'var(--bg-card)',
  border: '1px solid var(--border-med)',
  borderRadius: 10,
  color: 'var(--text)',
  fontFamily: 'DM Sans, sans-serif',
  fontSize: 16,
  outline: 'none',
};

function waitMessage(err) {
  const sec = Number(err.response?.headers?.['retry-after']);
  const min = Number.isFinite(sec) && sec > 0 ? Math.ceil(sec / 60) : 15;
  return `Too many attempts. Try again in ${min} ${min === 1 ? 'minute' : 'minutes'}.`;
}

export default function ChangePasswordModal({ onClose, onChanged }) {
  useBodyScrollLock();
  const [current,  setCurrent]  = useState('');
  const [next,     setNext]     = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const currentRef = useRef();

  const dirty = useRef(false);
  dirty.current = current !== '' || next !== '' || confirm !== '';
  const busy = useRef(false);
  busy.current = loading;

  // Escape and backdrop close straight away when nothing was typed, otherwise ask first.
  const requestClose = useCallback(() => {
    if (busy.current) return;
    if (dirty.current && !window.confirm('Discard your changes?')) return;
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [requestClose]);

  const lengthOk = next.length >= MIN_LEN;
  const differs  = next !== '' && next !== current;
  const canSubmit = current !== '' && next !== '' && confirm !== '' && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (!lengthOk)          return setError(`New password must be at least ${MIN_LEN} characters.`);
    if (next === current)   return setError('New password must be different from the current one.');
    if (next !== confirm)   return setError('New password and confirmation do not match.');

    setLoading(true);
    setError('');
    try {
      await changePassword(current, next);
      onChanged();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        setError('Current password is incorrect');
        setCurrent('');
        currentRef.current?.focus();
      } else if (status === 400 && err.response.data?.error) {
        setError(err.response.data.error);
      } else if (status === 429) {
        setError(waitMessage(err));
      } else {
        setError('Could not change password. Check your connection and try again.');
      }
      setLoading(false);
    }
  };

  const ruleColor = (ok) => (next === '' ? 'var(--text-dim)' : ok ? '#16A34A' : '#F87171');

  return (
    <div className="cpw-overlay" style={overlay} onClick={e => e.target === e.currentTarget && requestClose()}>
      <form className="cpw-modal" style={modal} onSubmit={handleSubmit} noValidate
            role="dialog" aria-modal="true" aria-labelledby="cpw-title">
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div id="cpw-title" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, fontSize: 15 }}>Change password</div>
          <button type="button" aria-label="Close" onClick={requestClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 18, cursor: 'pointer', minWidth: 44, minHeight: 44 }}>✕</button>
        </div>

        <div className="cpw-body" style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="cpw-current" style={labelStyle}>Current password</label>
            <input id="cpw-current" ref={currentRef} type="password" autoComplete="current-password" maxLength={200}
              value={current} onChange={e => { setCurrent(e.target.value); setError(''); }} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label htmlFor="cpw-new" style={labelStyle}>New password</label>
            <input id="cpw-new" type="password" autoComplete="new-password" maxLength={200}
              value={next} onChange={e => { setNext(e.target.value); setError(''); }} style={inputStyle} />
          </div>
          <ul style={{ margin: '0 0 16px', padding: 0, listStyle: 'none', fontSize: 12 }}>
            <li style={{ color: ruleColor(lengthOk) }}>At least {MIN_LEN} characters</li>
            <li style={{ color: ruleColor(differs) }}>Different from your current password</li>
          </ul>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="cpw-confirm" style={labelStyle}>Confirm new password</label>
            <input id="cpw-confirm" type="password" autoComplete="new-password" maxLength={200}
              value={confirm} onChange={e => { setConfirm(e.target.value); setError(''); }} style={inputStyle} />
          </div>

          {error && (
            <div role="alert" style={{
              background: 'rgba(248,113,113,0.1)',
              border: '1px solid rgba(248,113,113,0.3)',
              borderRadius: 8, color: '#F87171', fontSize: 13, padding: '8px 12px',
            }}>
              {error}
            </div>
          )}
        </div>

        <div className="cpw-footer" style={{ padding: '14px 20px', borderTop: '1px solid var(--border)', display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button type="button" onClick={requestClose} disabled={loading}
            style={{
              background: 'transparent', border: '1px solid var(--border-med)', borderRadius: 10,
              color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: 14,
              padding: '0 18px', minHeight: 44, cursor: loading ? 'not-allowed' : 'pointer',
            }}>
            Cancel
          </button>
          <button type="submit" disabled={!canSubmit}
            style={{
              background: canSubmit ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))' : '#E5E7EB',
              border: 'none', borderRadius: 10,
              color: canSubmit ? '#fff' : 'var(--text-dim)',
              fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 14,
              padding: '0 18px', minHeight: 44, cursor: canSubmit ? 'pointer' : 'not-allowed',
            }}>
            {loading ? 'Saving…' : 'Change password'}
          </button>
        </div>
      </form>
    </div>
  );
}
